// Eigene Kurzinfos beim Überfahren (Design-Runde 2, H): ersetzt die grauen Browser-Tooltips überall, wo ein Element ein
// `title` trägt. Steht am Ende des Textes ein Tastenkürzel in Klammern, „E-Mail schreiben (e)“, erscheint es als Taste
// neben dem Text. Eine Stelle für die ganze Oberfläche; kein Eingriff in die einzelnen Komponenten.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { tippZerlegen } from "../werkzeuge/tippText";

const VERZOEGERUNG_MS = 350;

interface Zeige {
  text: string;
  taste: string | null;
  x: number;
  y: number;
  unten: boolean;
}

export function Tooltips() {
  const [zeige, setZeige] = useState<Zeige | null>(null);
  const aktuell = useRef<{ el: HTMLElement; titel: string } | null>(null);
  const zeit = useRef<number | undefined>(undefined);

  useEffect(() => {
    const zurueck = () => {
      window.clearTimeout(zeit.current);
      const a = aktuell.current;
      if (a) {
        if (a.el.isConnected && !a.el.hasAttribute("title")) a.el.setAttribute("title", a.titel);
        aktuell.current = null;
      }
      setZeige(null);
    };
    const drueber = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      const ziel = (e.target as HTMLElement | null)?.closest?.("[title]") as HTMLElement | null;
      if (!ziel) {
        if (aktuell.current && !aktuell.current.el.contains(e.target as Node)) zurueck();
        return;
      }
      if (aktuell.current?.el === ziel) return;
      zurueck();
      const titel = ziel.getAttribute("title") ?? "";
      if (!titel.trim() || ziel.closest("[data-kein-tooltip]")) return;
      if (!ziel.hasAttribute("aria-label") && !(ziel.textContent ?? "").trim()) ziel.setAttribute("aria-label", titel);
      ziel.removeAttribute("title"); // das Browser-Tooltip bleibt aus, solange unser eigenes dran ist
      aktuell.current = { el: ziel, titel };
      zeit.current = window.setTimeout(() => {
        if (!ziel.isConnected) return zurueck();
        const r = ziel.getBoundingClientRect();
        const t = tippZerlegen(titel);
        const unten = r.top < 52;
        setZeige({ ...t, x: Math.round(r.left + r.width / 2), y: Math.round(unten ? r.bottom + 6 : r.top - 6), unten });
      }, VERZOEGERUNG_MS);
    };
    const raus = (e: PointerEvent) => {
      const a = aktuell.current;
      if (a && (!e.relatedTarget || !a.el.contains(e.relatedTarget as Node))) zurueck();
    };
    document.addEventListener("pointerover", drueber, true);
    document.addEventListener("pointerout", raus, true);
    document.addEventListener("pointerdown", zurueck, true);
    document.addEventListener("keydown", zurueck, true);
    window.addEventListener("scroll", zurueck, true);
    window.addEventListener("blur", zurueck);
    return () => {
      document.removeEventListener("pointerover", drueber, true);
      document.removeEventListener("pointerout", raus, true);
      document.removeEventListener("pointerdown", zurueck, true);
      document.removeEventListener("keydown", zurueck, true);
      window.removeEventListener("scroll", zurueck, true);
      window.removeEventListener("blur", zurueck);
      zurueck();
    };
  }, []);

  if (!zeige) return null;
  const maxBreite = 280;
  const links = Math.max(8 + maxBreite / 2, Math.min(window.innerWidth - 8 - maxBreite / 2, zeige.x));
  return createPortal(
    <div className={`tooltip${zeige.unten ? " tooltip--unten" : ""}`} role="tooltip" style={{ left: links, top: zeige.y }}>
      <span className="tooltip__text">{zeige.text}</span>
      {zeige.taste ? <kbd className="tooltip__taste">{zeige.taste}</kbd> : null}
    </div>,
    document.body,
  );
}
